import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { AuthApiError, register } from '../../auth/authApi'
import { getLocale } from '../../i18n'
import type { AuthResponse } from '../../auth/types'
import { Button, Field, Icon, I } from '../../components/ui'
import { AuthLayout } from './AuthLayout'
import { GoogleAuthFlow } from './GoogleAuthFlow'
import { passwordMeetsPolicy, passwordPolicyChecks, type PasswordPolicyCheck } from './passwordPolicy'

type AuthContext = 'zid-oauth' | 'salla-oauth' | 'amazon-oauth' | 'zoho-oauth'

interface RegisterPageProps {
  authContext?: AuthContext
  onRegisterSuccess: (response: AuthResponse) => void
  onLogin: () => void
  onHome?: () => void
  onPrivacy: () => void
  onTerms: () => void
}

function PasswordChecklist({ checks }: { checks: PasswordPolicyCheck[] }) {
  const { t } = useTranslation()
  return (
    <div style={{ display: 'grid', gap: 7, padding: 12, borderRadius: 12, background: 'var(--ink-50)', border: '1px solid var(--border-subtle)' }}>
      {checks.map(check => (
        <div key={check.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: check.ok ? 'var(--success)' : 'var(--fg-subtle)', fontWeight: check.ok ? 700 : 500 }}>
          <Icon size={14} color={check.ok ? 'var(--success)' : 'var(--fg-disabled)'}>{check.ok ? I.check : I.clock}</Icon>
          {t(`auth.policy.${check.key}`)}
        </div>
      ))}
    </div>
  )
}

function contextMessageKey(authContext: AuthContext): string {
  if (authContext === 'zid-oauth') return 'auth.register.contexts.zidOAuth'
  if (authContext === 'salla-oauth') return 'auth.register.contexts.sallaOAuth'
  if (authContext === 'amazon-oauth') return 'auth.register.contexts.amazonOAuth'
  return 'auth.register.contexts.zohoOAuth'
}

function subtitleKey(authContext: AuthContext | undefined): string {
  if (authContext === 'zid-oauth') return 'auth.register.contexts.zidOAuthSubtitle'
  if (authContext === 'salla-oauth') return 'auth.register.contexts.sallaOAuthSubtitle'
  if (authContext === 'amazon-oauth') return 'auth.register.contexts.amazonOAuthSubtitle'
  if (authContext === 'zoho-oauth') return 'auth.register.contexts.zohoOAuthSubtitle'
  return 'auth.register.subtitle'
}

export function RegisterPage({ authContext, onRegisterSuccess, onLogin, onHome, onPrivacy, onTerms }: RegisterPageProps) {
  const { t } = useTranslation()
  const [fullName, setFullName] = useState('')
  const [organizationName, setOrganizationName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [googleActive, setGoogleActive] = useState(false)

  const checks = passwordPolicyChecks(password, { fullName, email })

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)

    if (!fullName.trim() || !organizationName.trim() || !email.trim() || !password) {
      setError(t('auth.register.errors.missingFields'))
      return
    }

    if (!passwordMeetsPolicy(checks)) {
      setError(t('auth.register.errors.policy'))
      return
    }

    setLoading(true)
    try {
      const response = await register({
        fullName: fullName.trim(),
        organizationName: organizationName.trim(),
        email: email.trim(),
        password,
        locale: getLocale(),
      })
      onRegisterSuccess(response)
    } catch (caught) {
      if (caught instanceof AuthApiError) {
        if (caught.code === 'EMAIL_ALREADY_REGISTERED') {
          setError(t('auth.register.errors.emailTaken'))
        } else if (caught.code === 'PASSWORD_POLICY_FAILED') {
          setError(t('auth.register.errors.policyServer'))
        } else {
          setError(t('auth.register.errors.network'))
        }
      } else {
        setError(t('auth.register.errors.network'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout
      title={t('auth.register.title')}
      subtitle={t(subtitleKey(authContext))}
      heroTitle={t('auth.register.hero.title')}
      heroSubtitle={t('auth.register.hero.subtitle')}
      onHome={onHome}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, alignItems: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
          <span>{t('auth.register.haveAccount')}</span>
          <button type="button" onClick={onLogin} style={{ border: 'none', background: 'transparent', color: 'var(--brand-700)', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
            {t('auth.register.signIn')}
          </button>
        </div>
      )}
    >
      <GoogleAuthFlow intent="register" onSuccess={onRegisterSuccess} onActiveChange={setGoogleActive} disabled={loading} />
      {!googleActive && <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 15 }}>
        {authContext && (
          <div style={{ padding: '12px 14px', borderRadius: 12, background: 'var(--brand-100)', border: '1px solid var(--brand-300)', color: 'var(--brand-900)', fontSize: 13.5, lineHeight: 1.5, fontWeight: 700 }}>
            {t(contextMessageKey(authContext))}
          </div>
        )}

        <Field label={t('auth.register.fullName')} placeholder={t('auth.register.fullNamePlaceholder')} value={fullName} onChange={event => setFullName(event.target.value)} />
        <Field label={t('auth.register.organizationName')} placeholder={t('auth.register.organizationPlaceholder')} value={organizationName} onChange={event => setOrganizationName(event.target.value)} />
        <Field label={t('auth.register.email')} type="email" placeholder={t('auth.register.emailPlaceholder')} value={email} onChange={event => setEmail(event.target.value)} />
        <Field label={t('auth.register.password')} type="password" placeholder={t('auth.register.passwordPlaceholder')} value={password} onChange={event => setPassword(event.target.value)} />

        <PasswordChecklist checks={checks} />

        {error && (
          <div style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--danger-100)', color: 'var(--danger)', fontSize: 13, lineHeight: 1.45 }}>
            {error}
          </div>
        )}

        <Button type="submit" size="lg" disabled={loading} style={{ justifyContent: 'center', width: '100%' }}>
          {loading ? t('auth.register.submitting') : t('auth.register.submit')}
        </Button>
      </form>}
      <p style={{ color: 'var(--fg-subtle)', fontSize: 12, lineHeight: 1.6, margin: 'var(--sp-3) 0 0', textAlign: 'center' }}>
        {t('auth.register.legal.prefix')}{' '}
        <a href="/terms" onClick={(event) => { event.preventDefault(); onTerms() }} style={{ color: 'var(--brand-700)', fontWeight: 700 }}>{t('auth.register.legal.terms')}</a>{' '}
        {t('auth.register.legal.middle')}{' '}
        <a href="/privacy" onClick={(event) => { event.preventDefault(); onPrivacy() }} style={{ color: 'var(--brand-700)', fontWeight: 700 }}>{t('auth.register.legal.privacy')}</a>.
      </p>
    </AuthLayout>
  )
}
